import React, { lazy, Suspense, useEffect } from "react";
import "../global.css";

import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import CustomLoader from "./components/common/custom-loader";
import { trackReferral } from "./api";
import ProtectedRoute from "./ProtectedRoute";

const CongratulationPage = lazy(
  () => import("./components/auth/setup-account/congratulations")
);

const HomePage = lazy(() => import("./components/auth/setup-account/homepage"));

const AddBookForm = lazy(
  () => import("./components/auth/setup-account/addBookForm")
);
const SetupAccountFormPage = lazy(
  () => import("./components/auth/setup-account/setupAccountForm")
);
const SubscriptionInitialPage = lazy(
  () => import("./components/auth/setup-account/subscriptionInitial")
);
const UploadFormPage = lazy(
  () => import("./components/auth/setup-account/uploadForm")
);
const BookListingPage = lazy(
  () => import("./components/auth/setup-account/bookListing")
);
const BookReviews = lazy(
  () => import("./components/auth/setup-account/bookReviews")
);

const Comparatives = lazy(
  () => import("./components/auth/setup-account/comparatives")
);
const Synopsis = lazy(() => import("./components/auth/setup-account/synopsis"));

const Critique = lazy(() => import("./components/auth/setup-account/critique"));

const Partners = lazy(() => import("./components/auth/setup-account/partners"));

const SelfPublishingAdvance = lazy(
  () => import("./components/auth/setup-account/selfPublishingAdvance")
);
const UserSettings = lazy(
  () => import("./components/auth/setup-account/userSettings")
);
const Login = lazy(() => import("./components/auth/login/login"));
const Logout = lazy(() => import("./components/auth/logout/logout"));
const MasterDetails = lazy(() => import("./components/client/master-details"));
const Upgrade = lazy(() => import("./components/auth/setup-account/upgrade"));

// Keep all your lazy imports the same
const SignupPage = lazy(() => import("./views/auth/signup"));
const StepperForm = lazy(() => import("./views/auth/step-form"));
const ForgotPassword = lazy(
  () => import("./components/auth/setup-account/forgotPassword")
);
const ResetPassword = lazy(
  () => import("./components/auth/setup-account/resetPassword")
);

const ErrorPage = lazy(() => import("./ErrorPage"));

// Create a wrapper for protected routes
const ProtectedRouteWrapper = ({ Component }) => {
  return (
    <Suspense fallback={<CustomLoader />}>
      <ProtectedRoute element={Component} />
    </Suspense>
  );
};

// Create a layout component for routes with the Suspense
const SuspenseLayout = () => {
  return (
    <Suspense fallback={<CustomLoader />}>
      <Outlet />
    </Suspense>
  );
};

// MasterDetails with outlet for nested routes
const MasterDetailsLayout = () => {
  const MasterDetails = lazy(
    () => import("./components/client/master-details")
  );
  return (
    <Suspense fallback={<CustomLoader />}>
      <MasterDetails />
    </Suspense>
  );
};

export default function App() {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let utmSource = urlParams.get("utm_source");
    let utmMedium = urlParams.get("utm_medium");
    let utmCampaign = urlParams.get("utm_campaign");
    const referrer = document.referrer;

    if (window.location.origin === "https://bowker.storywisenetwork.com") {
      utmSource = "Bowker";
      utmMedium = "referral";
      utmCampaign = "email";
    }

    if (!sessionStorage.getItem("referral_id") && utmSource) {
      trackReferral(utmSource, utmMedium, utmCampaign, referrer).then(
        (referralId) => {
          sessionStorage.setItem("referral_id", referralId);
        }
      );
    }
  }, []);

  // Define router using the object-based format
  const router = createBrowserRouter([
    {
      path: "/",
      element: <ProtectedRouteWrapper Component={HomePage} />,
      errorElement: <ErrorPage />,
    },
    {
      element: <SuspenseLayout />,
      errorElement: <ErrorPage />,
      children: [
        { path: "/invited", element: <SignupPage /> },
        { path: "/account-create", element: <StepperForm /> },
        { path: "/congratulations", element: <CongratulationPage /> },
        { path: "/Login", element: <Login /> },
        { path: "/logout", element: <Logout /> },
        { path: "/forgot-password", element: <ForgotPassword /> },
        { path: "/reset-password", element: <ResetPassword /> },
        { path: "/setupAccountForm", element: <SetupAccountFormPage /> },
        { path: "/subscriptionInitial", element: <SubscriptionInitialPage /> },
        { path: "/uploadForm", element: <UploadFormPage /> },
        {
          path: "/addBook",
          element: <ProtectedRouteWrapper Component={AddBookForm} />,
        },
        {
          path: "/userSettings",
          element: <ProtectedRouteWrapper Component={UserSettings} />,
        },
        {
          path: "/upgrade",
          element: <ProtectedRouteWrapper Component={Upgrade} />,
        },
        // Book routes with nested children
        {
          path: "/books/:bookId",
          element: <ProtectedRouteWrapper Component={MasterDetailsLayout} />,
          children: [
            { index: true, element: <BookListingPage /> },
            { path: "critique", element: <Critique /> },
            { path: "synopsis", element: <Synopsis /> },
            { path: "comparatives", element: <Comparatives /> },
            { path: "reviews", element: <BookReviews /> },
            { path: "selfpub", element: <SelfPublishingAdvance /> },
            { path: "partners", element: <Partners /> },
          ],
        },
      ],
    },
  ]);

  return (
    <>
      <RouterProvider router={router} />
    </>
  );
}
